'use client';

import { useState, useEffect } from 'react';
import { Play } from 'lucide-react';
import Link from 'next/link';
import AudioBookCreator from '@/components/audio-book/audio-book-form';
import AudioBookVideoPlayer from '@/components/audio-book/audio-book-video-player';
import VoiceTrainerModal from '@/components/audio-book/voice-trainer-modal';
import CreateWizard from '@/components/create-wizard/CreateWizard';
import CreateWizardMobile from '@/components/create-wizard/CreateWizardMobile';

// Free Featured Sample Videos Component
function FeaturedSamplesSection() {
  const [freeBooks, setFreeBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch featured sample books from API
    fetch('/api/audio-book/samples')
      .then(res => res.json())
      .then(data => {
        setFreeBooks(data.audioBooks || []);
      })
      .catch(error => {
        console.error('Error fetching sample books:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <div style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '2rem 1.5rem',
      position: 'relative',
      zIndex: 1
    }}>
      {/* Section Header - 2026 Award-Winning Design */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          fontWeight: 900,
          marginBottom: '1rem',
          lineHeight: 1.1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          letterSpacing: '-0.02em',
        }}>
          Watch Free Samples
        </h2>
        <p style={{
          fontSize: 'clamp(1.0625rem, 2vw, 1.25rem)',
          color: '#64748b',
          maxWidth: '650px',
          margin: '0 auto',
          lineHeight: 1.6,
          fontWeight: 500,
        }}>
          Experience the magic before you create! See our AI-powered storytelling in action.
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '1.125rem', color: '#64748b' }}>
            Loading free samples...
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && freeBooks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '1.125rem', color: '#64748b' }}>
            No sample books available yet. Check back soon!
          </div>
        </div>
      )}

      {/* Sample Videos Grid - 2026 Premium Design */}
      {!loading && freeBooks.length > 0 && (
        <div 
          className="sample-videos-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
          }}>
          {freeBooks.map((book, index) => (
          <Link
            key={book.id}
            href={`/audio-book/${book.id}`}
            style={{ textDecoration: 'none' }}
          >
            <div
              className="premium-sample-card"
              style={{
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                position: 'relative',
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s backwards`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 20px 60px rgba(102, 126, 234, 0.25), 0 8px 24px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
              }}
            >
              {/* Cover Image with Play Button */}
              <div style={{
                position: 'relative',
                paddingTop: '110%',
                background: book.coverImageUrl
                  ? `url(${book.coverImageUrl}) center/cover`
                  : 'linear-gradient(135deg, #FFB4D1, #FFA8C5)',
                overflow: 'hidden',
              }}>
                {/* Play Button Overlay */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0))',
                  transition: 'background 0.3s ease',
                }}
                className="play-overlay"
                >
                  <div style={{
                    width: '70px',
                    height: '70px',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.3s ease',
                    border: '2px solid rgba(255, 255, 255, 0.5)',
                  }}>
                    <Play size={32} color="#667eea" fill="#667eea" style={{ marginLeft: '4px' }} />
                  </div>
                </div>

                {/* FREE Badge */}
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  left: '1rem',
                  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                  padding: '0.5rem 1rem',
                  borderRadius: '50px',
                  fontSize: '0.75rem',
                  fontWeight: 900,
                  color: 'white',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  boxShadow: '0 4px 16px rgba(255, 215, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                }}>
                  <span style={{ fontSize: '1rem' }}>🎁</span>
                  FREE
                </div>
              </div>

              {/* Book Info */}
              <div style={{ padding: '1.75rem' }}>
                <h3 style={{
                  color: '#1a202c',
                  fontSize: '1.25rem',
                  fontWeight: 800,
                  margin: '0 0 0.75rem 0',
                  lineHeight: 1.3,
                  letterSpacing: '-0.01em',
                }}>
                  {book.title}
                </h3>
                <p style={{
                  color: '#64748b',
                  fontSize: '0.9375rem',
                  margin: '0 0 1.5rem 0',
                  lineHeight: 1.6,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  fontWeight: 500,
                }}>
                  {book.prompt}
                </p>
                <div style={{
                  padding: '0.875rem 1.5rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundSize: '200% 100%',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.625rem',
                  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease',
                }}
                className="watch-btn"
                >
                  <Play size={18} />
                  Watch Story
                </div>
              </div>
            </div>
          </Link>
        ))}
        </div>
      )}
      
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .premium-sample-card:hover .play-overlay {
          background: linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.05)) !important;
        }
        
        .premium-sample-card:hover .watch-btn {
          background-position: 100% 0;
          box-shadow: 0 6px 24px rgba(102, 126, 234, 0.5);
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
}

export default function AudioBookCreatePage() {
  const [showVoiceModal, setShowVoiceModal] = useState(false);

  // Render both versions and use CSS to show/hide based on screen size
  // This avoids hydration mismatches and SSR issues
  return (
    <>
      {/* Global styles for mobile/desktop switching */}
      <style jsx global>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float-gentle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .mobile-hero-gradient {
          background: linear-gradient(180deg, 
            rgba(253, 251, 255, 0.95) 0%, 
            rgba(255, 249, 245, 0.9) 50%, 
            rgba(255, 245, 240, 0.85) 100%
          );
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }

        /* Mobile-first: Show mobile, hide desktop by default */
        .mobile-version {
          display: block !important;
        }

        .desktop-version {
          display: none !important;
        }

        /* Desktop: Hide mobile, show desktop */
        @media (min-width: 769px) {
          .mobile-version {
            display: none !important;
          }
          .desktop-version {
            display: block !important;
          }
        }

        /* Hide overlay on mobile to avoid covering video controls */
        @media (max-width: 768px) {
          .hero-video-overlay {
            display: none !important;
          }
          
          /* Mobile-optimized video grid */
          .sample-videos-grid {
            grid-template-columns: 1fr !important;
            gap: 1.25rem !important;
            padding: 0 0.5rem;
          }
        }
        
        /* Small mobile screens */
        @media (max-width: 480px) {
          .sample-videos-grid {
            gap: 1rem !important;
          }
        }
      `}</style>

      {/* 🎨 2026 Award-Winning Mobile Version - Hidden on Desktop */}
      <div className="mobile-version" suppressHydrationWarning>

        {/* Hero Video Section for Mobile */}
        <div style={{ 
          marginTop: '60px',
          padding: '0.75rem',
          position: 'relative',
        }}>
          <div style={{ position: 'relative' }}>
            <AudioBookVideoPlayer 
              videoSrc="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/audio-book-generation-amayastories-com.mp4"
              posterSrc="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/Amaya%20Story.webp"
            />
            
            {/* Overlay with heading on video */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(to bottom, rgba(255,245,235,0.85) 0%, rgba(255,245,235,0.6) 50%, transparent 100%)',
              pointerEvents: 'none',
              padding: '1rem',
            }}>
              <h1 style={{ 
                fontSize: 'clamp(1.5rem, 6vw, 2rem)', 
                fontWeight: 900, 
                lineHeight: 1.1,
                margin: '0 0 0.5rem 0',
                background: 'linear-gradient(135deg, #D64481 0%, #5E9FBE 50%, #9570BC 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.02em',
                textAlign: 'center',
                filter: 'brightness(0.95)',
              }}>
                Create Your Unique Audio Picture Book
              </h1>

              {/* Subtitle */}
              <p style={{
                fontSize: '0.875rem',
                color: '#333',
                margin: 0,
                fontWeight: 700,
                lineHeight: 1.4,
                textAlign: 'center',
                textShadow: '0 2px 4px rgba(255,255,255,0.6)',
              }}>
                Professional narration in minutes 🎧✨
              </p>
            </div>
          </div>
        </div>

        <CreateWizardMobile />
        
        {/* Featured Sample Videos - Mobile Optimized */}
        <div style={{ 
          background: 'linear-gradient(180deg, #FDFBFF 0%, #FFF9F5 50%, #FFF5F0 100%)',
          paddingTop: '1rem',
          paddingBottom: '3rem',
        }}>
          <FeaturedSamplesSection />
        </div>
      </div>

      {/* Desktop Version - Hidden on Mobile */}
      <div className="desktop-version" suppressHydrationWarning>
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(180deg, #FFF9F5 0%, #FFF5F0 30%, #FFEFE8 60%, #FFE8E0 100%)',
        paddingTop: '10px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Premium Decorative Background Elements - Modern SVG Style */}
        
        {/* Top Left - Sparkle Cluster */}
        <div style={{
          position: 'absolute',
          top: '8%',
          left: '5%',
          width: '120px',
          height: '120px',
          opacity: 0.08,
          animation: 'float 8s ease-in-out infinite',
        }}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M50 0 L55 45 L100 50 L55 55 L50 100 L45 55 L0 50 L45 45 Z" fill="#C5B4FF" />
            <path d="M75 15 L78 35 L95 38 L78 41 L75 58 L72 41 L55 38 L72 35 Z" fill="#B5F2E0" opacity="0.7" />
          </svg>
        </div>
        
        {/* Top Right - Book Icon */}
        <div style={{
          position: 'absolute',
          top: '12%',
          right: '8%',
          width: '100px',
          height: '100px',
          opacity: 0.08,
          animation: 'float 7s ease-in-out infinite 1.5s',
        }}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 15 C20 12 22 10 25 10 L75 10 C78 10 80 12 80 15 L80 85 C80 88 78 90 75 90 L25 90 C22 90 20 88 20 85 Z" fill="#A8D8E6" />
            <path d="M25 15 L75 15 L75 85 L25 85 Z" fill="white" opacity="0.3" />
            <path d="M50 10 L50 90" stroke="#7DD8E8" strokeWidth="2" opacity="0.5" />
          </svg>
        </div>
        
        {/* Middle Left - Music Note */}
        <div style={{
          position: 'absolute',
          top: '45%',
          left: '3%',
          width: '90px',
          height: '90px',
          opacity: 0.08,
          animation: 'float 9s ease-in-out infinite 2s',
        }}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="30" cy="75" rx="15" ry="10" fill="#F4A4C4" />
            <path d="M45 75 L45 25 L75 20 L75 65" stroke="#F4A4C4" strokeWidth="5" strokeLinecap="round" />
            <ellipse cx="75" cy="70" rx="15" ry="10" fill="#F4A4C4" />
          </svg>
        </div>
        
        {/* Bottom Right - Cloud */}
        <div style={{
          position: 'absolute',
          bottom: '15%',
          right: '5%',
          width: '130px',
          height: '130px',
          opacity: 0.08,
          animation: 'float 10s ease-in-out infinite 1s',
        }}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="35" cy="50" rx="20" ry="15" fill="#D8B5E8" />
            <ellipse cx="55" cy="45" rx="25" ry="20" fill="#D8B5E8" />
            <ellipse cx="70" cy="55" rx="18" ry="15" fill="#D8B5E8" />
            <rect x="20" y="50" width="60" height="20" fill="#D8B5E8" />
          </svg>
        </div>
        
        {/* Bottom Left - Heart */}
        <div style={{
          position: 'absolute',
          bottom: '25%',
          left: '8%',
          width: '85px',
          height: '85px',
          opacity: 0.08,
          animation: 'float 7.5s ease-in-out infinite 0.5s',
        }}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M50 85 C50 85 15 60 15 35 C15 20 25 10 35 10 C42 10 48 15 50 20 C52 15 58 10 65 10 C75 10 85 20 85 35 C85 60 50 85 50 85 Z" fill="#FFB5A7" />
          </svg>
        </div>

        {/* Hero Video Section with Overlay */}
        <div className="container" style={{ 
          maxWidth: '1000px', 
          margin: '0 auto', 
          padding: '2rem 1.5rem 1rem',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{ position: 'relative', marginBottom: '2rem' }}>
            <AudioBookVideoPlayer 
              videoSrc="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/audio-book-generation-amayastories-com.mp4"
              posterSrc="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/Amaya%20Story.webp"
            />
            
            {/* Non-intrusive overlay on video (hidden on mobile to avoid covering controls) */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(to bottom, rgba(255,245,235,0.9) 0%, rgba(255,245,235,0.7) 50%, transparent 100%)',
              pointerEvents: 'none',
              padding: '2rem',
            }}
            className="hero-video-overlay"
            >
              <h1 style={{
                fontSize: 'clamp(1.75rem, 5vw, 3rem)',
                fontWeight: 800,
                marginBottom: '0.75rem',
                background: 'linear-gradient(135deg, #D64481 0%, #5E9FBE 50%, #9570BC 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center',
                lineHeight: 1.2,
                filter: 'brightness(0.95)',
              }}>
                Create Your Audio Storybook
              </h1>
              <p style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                color: '#333',
                textAlign: 'center',
                marginBottom: 0,
                maxWidth: '600px',
                fontWeight: 700,
                textShadow: '0 2px 4px rgba(255,255,255,0.6)',
              }}>
                Education • Bilingual Learning • Montessori-Inspired
              </p>
            </div>
          </div>
        </div>

        {/* Wizard Component */}
        <CreateWizard />

        {/* Free Sample Videos Section */}
        <FeaturedSamplesSection />
      </div>
      </div>
    </>
  );
}
